import { useState, useEffect, useRef } from "react";
import { Container } from "@/components/Container";
import LongFormContentCard from "@/components/LongFormContentCard";
import EditContentDialog from "@/components/EditContentDialog";
import EditWithFeedbackDialog from "@/components/EditWithFeedbackDialog";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useMemo } from "react";
import { H6 } from "@/components/ui/h6";
import YouTubeInput from "@/components/YouTubeInput";
import {
  LoaderCircle,
  Youtube,
  History,
  FileText,
  Sparkles,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  contentQueries,
  useEditContentMutation,
  useDeleteContentMutation,
  usePublishContentMutation,
  useCreateContentMutation,
} from "@/api/longform";

type ContentType = "topic" | "media";
type FormatType =
  | "essay"
  | "report"
  | "analysis"
  | "opinion"
  | "thought_piece"
  | "research_paper"
  | "editorial";
type LengthType = "short" | "medium" | "long" | "comprehensive" | "custom";

const MEDIA_LOADING_MESSAGES = [
  "Analyzing media content...",
  "Extracting key insights...",
  "Researching context and background...",
  "This may take a few minutes for longer content...",
  "Generating comprehensive analysis...",
  "Crafting thoughtful perspective...",
  "Finalizing content structure...",
];
const TOPIC_LOADING_MESSAGES = [
  "Analyzing topic...",
  "Starting Agent Research...",
  "Collecting relevant information...",
  "Exploring multiple perspectives...",
  "Developing comprehensive insights...",
  "Structuring clear narrative...",
  "Creating thought leadership content...",
  "Finalizing with unique perspective...",
];

function LongFormContentComponent({
  personaId,
  personaData,
}: {
  personaId: string;
  personaData: any;
}) {
  const { toast } = useToast();

  const [selectedType, setSelectedType] = useState<ContentType>("topic");
  const [topic, setTopic] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("video");
  const [formatType, setFormatType] = useState<FormatType>("analysis");
  const [lengthType, setLengthType] = useState<LengthType>("medium");
  const [customLength, setCustomLength] = useState<number>(2000);
  const [wordCount, setWordCount] = useState<number>(400); // Default medium length
  const [userGuidance, setUserGuidance] = useState("");
  const [previewStatus, setPreviewStatus] = useState<
    "draft" | "published" | null
  >("draft");

  const [editState, setEditState] = useState<{
    type: "ai" | "edit";
    contentId: string;
  } | null>(null);

  const [loadingStep, setLoadingStep] = useState<number | null>(null);
  const loadingMessages =
    selectedType === "media" ? MEDIA_LOADING_MESSAGES : TOPIC_LOADING_MESSAGES;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: contentData, isLoading: isContentLoading } = useQuery(
    contentQueries.contentList(personaId, { status: previewStatus }),
  );

  const createContentMutation = useCreateContentMutation();
  const publishContentMutation = usePublishContentMutation();
  const deleteContentMutation = useDeleteContentMutation();
  const editContentMutation = useEditContentMutation();

  const isLoading = loadingStep !== null || publishContentMutation.isPending;

  // Sort content in the frontend instead of backend
  const sortedContent = useMemo(() => {
    if (!contentData?.data) return [];

    // Filter out deleted content
    const filteredContent = contentData.data.filter(
      (item) => item.status !== "deleted",
    );

    return [...filteredContent].sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [contentData]);

  // Effect to update word count based on length type
  useEffect(() => {
    switch (lengthType) {
      case "short":
        setWordCount(200);
        break;
      case "medium":
        setWordCount(400);
        break;
      case "long":
        setWordCount(800);
        break;
      case "comprehensive":
        setWordCount(2000);
        break;
      case "custom":
        setWordCount(customLength);
        break;
    }
  }, [lengthType, customLength]);

  const stopLoadingMessages = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setLoadingStep(null);
  };

  const handleCreateContent = async () => {
    if (!personaId) {
      toast({ description: "Please select an agent first." });
      return;
    }

    setLoadingStep(0);
    intervalRef.current = setInterval(() => {
      setLoadingStep((prev) => ((prev ?? -1) + 1) % loadingMessages.length);
    }, 2000);

    if (selectedType === "topic" && !topic) {
      toast({ description: "Please enter a topic." });
      return;
    } else if (selectedType === "media" && !mediaUrl) {
      toast({ description: "Please enter a media URL." });
      return;
    }

    const variables =
      selectedType === "topic"
        ? {
            personaId,
            type: selectedType,
            content: {
              topic,
              format_type: formatType,
              length_type: lengthType,
              custom_length: lengthType === "custom" ? customLength : undefined,
              user_guidance: userGuidance,
            },
          }
        : {
            personaId,
            type: selectedType,
            content: {
              media_url: mediaUrl,
              media_type: mediaType,
              format_type: formatType,
              length_type: lengthType,
              custom_length: lengthType === "custom" ? customLength : undefined,
              user_guidance: userGuidance,
            },
          };

    createContentMutation.mutate(variables, {
      onSuccess: () => toast({ description: "Content created successfully!" }),
      onError: (error: any) =>
        toast({
          description:
            error.response?.data?.detail ||
            "Error creating content. Please try again.",
          variant: "destructive",
        }),
      onSettled: () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setLoadingStep(null);
      },
    });

    setTopic("");
    setMediaUrl("");
    setUserGuidance("");
  };

  const handleStatusChange = async (
    contentId: string,
    newStatus: "draft" | "published",
  ) => {
    // Find the current content to preserve all existing data
    const currentContent = contentData?.data?.find(
      (item) => item.content_id === contentId,
    );

    if (!currentContent) {
      const error = new Error("Content not found");
      console.error("Status change error:", error);
      throw error;
    }

    // Create metadata object that preserves existing metadata
    const updatedMetadata = {
      ...(currentContent.metadata || {}),
      status: newStatus,
    };

    editContentMutation.mutate(
      {
        personaId,
        contentId,
        content: { metadata: updatedMetadata },
      },
      {
        onSuccess: () => toast({ description: "Status updated successfully!" }),
        onError: (error: any) => {
          console.error("Status change error:", error);
          toast({
            description: "Error changing status. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Container className="max-w-full px-4 sm:px-6 lg:px-16">
      {/* Loading Dialog with Process in Background option */}
      <Dialog open={isLoading}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center">
            <LoaderCircle className="h-12 w-12 animate-spin m-auto" />
            <p className="mt-4 text-center">
              {loadingStep != null ? loadingMessages[loadingStep] : ""}
            </p>
            <div className="ml-auto mt-10">
              <Button variant="outline" onClick={() => stopLoadingMessages()}>
                Process in Background
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      {editState?.contentId && (
        <>
          <EditContentDialog
            open={editState?.type === "edit"}
            onOpenChange={(open) =>
              setEditState((state) => (open ? state : null))
            }
            contentId={editState.contentId}
            personaId={personaId}
          />
          <EditWithFeedbackDialog
            open={editState?.type === "ai"}
            onOpenChange={(open) =>
              setEditState((state) => (open ? state : null))
            }
            contentId={editState.contentId}
            personaId={personaId}
          />
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        {/* Left Column - Content Creation */}
        <div className="flex flex-col space-y-6 col-span-2">
          <div className="flex justify-between items-center mb-2">
            <H6 className="flex items-center">
              <Sparkles className="h-5 w-5 mr-2" />
              Create Content
            </H6>
            <div className="flex gap-2">
              <Button
                variant={selectedType === "topic" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("topic")}
              >
                <FileText className="mr-2 h-4 w-4" />
                Topic
              </Button>
              <Button
                variant={selectedType === "media" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType("media")}
              >
                <Youtube className="mr-2 h-4 w-4" />
                Media
              </Button>
            </div>
          </div>

          {/* Descriptive Guidance Text */}
          {selectedType === "topic" && (
            <p className="text-muted-foreground text-sm text-center max-w-2xl mx-auto mb-2">
              A specialized team of AI research agents will collaborate with
              your agent to craft original content based on your agent's unique
              personality, interests, & worldview.
            </p>
          )}

          {selectedType === "media" && (
            <p className="text-muted-foreground text-sm text-center max-w-2xl mx-auto mb-2">
              Our advanced AI research collective will work alongside your agent
              to analyze media content through the lens of your agent's distinct
              perspective and expertise.
            </p>
          )}

          {/* Create Content Form */}
          <div className="space-y-4 p-6 bg-card rounded-lg shadow border ">
            {selectedType === "topic" ? (
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Enter your topic..."
                disabled={isLoading}
              />
            ) : (
              <>
                <div className="grid grid-cols-4 gap-4 items-center">
                  <Label className="col-span-1">Media Type:</Label>
                  <Select
                    value={mediaType}
                    onValueChange={setMediaType}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Media Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="podcast">Podcast</SelectItem>
                      <SelectItem value="article">Article</SelectItem>
                      <SelectItem value="research_paper">
                        Research Paper
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {mediaType === "video" ? (
                  <YouTubeInput
                    value={mediaUrl}
                    onChange={setMediaUrl}
                    isLoading={isLoading}
                  />
                ) : (
                  <Input
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder={`Enter ${mediaType} URL...`}
                    disabled={isLoading}
                  />
                )}
              </>
            )}

            <div className="grid grid-cols-4 gap-4 items-center">
              <Label className="col-span-1">Format:</Label>
              <Select
                value={formatType}
                onValueChange={(value) => setFormatType(value as FormatType)}
                disabled={isLoading}
              >
                <SelectTrigger className="col-span-2">
                  <SelectValue placeholder="Select Format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analysis">Analysis</SelectItem>
                  <SelectItem value="opinion">Opinion</SelectItem>
                  <SelectItem value="thought_piece">Thought Piece</SelectItem>
                  <SelectItem value="essay">Essay</SelectItem>
                  <SelectItem value="report">Report</SelectItem>
                  <SelectItem value="research_paper">Research Paper</SelectItem>
                  <SelectItem value="editorial">Editorial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 gap-4 items-center">
              <Label className="col-span-1">Length:</Label>
              <Select
                value={lengthType}
                onValueChange={(value) => setLengthType(value as LengthType)}
                disabled={isLoading}
              >
                <SelectTrigger className="col-span-2">
                  <SelectValue placeholder="Select Length" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (~200 words)</SelectItem>
                  <SelectItem value="medium">Medium (~400 words)</SelectItem>
                  <SelectItem value="long">Long (~800 words)</SelectItem>
                  <SelectItem value="comprehensive">
                    Comprehensive (~2000 words)
                  </SelectItem>
                  <SelectItem value="custom">Custom Length</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {lengthType === "custom" && (
              <div className="flex items-center gap-4">
                <Label>Custom Word Count: {customLength}</Label>
                <Slider
                  min={200}
                  max={7000}
                  step={100}
                  value={[customLength]}
                  onValueChange={(values) => setCustomLength(values[0])}
                  className="w-48"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Guidance (Optional):</Label>
              <Textarea
                value={userGuidance}
                onChange={(e) => setUserGuidance(e.target.value)}
                placeholder="Add specific guidance for style, structure, focus areas, or key points to include..."
                rows={3}
                disabled={isLoading}
                className="resize-none"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <span className="font-medium">Summary: </span>
                  <span className="text-muted-foreground">
                    {formatType.replace("_", " ")} • {wordCount} words
                  </span>
                </div>
              </div>
              <Button
                onClick={handleCreateContent}
                disabled={isLoading}
                className="w-full"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Content
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Content Library */}
        <div className="space-y-4 col-span-3">
          <div className="flex justify-between items-center">
            <H6 className="flex items-center">
              <History className="h-5 w-5 mr-2" />
              Content Library
            </H6>
            <div className="flex gap-2">
              <Button
                variant={previewStatus === "draft" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewStatus("draft")}
              >
                Drafts
              </Button>
              <Button
                variant={previewStatus === "published" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewStatus("published")}
              >
                Published
              </Button>
              <Button
                variant={previewStatus === null ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewStatus(null)}
              >
                All
              </Button>
            </div>
          </div>

          {isContentLoading ? (
            <div className="flex justify-center py-8">
              <LoaderCircle className="h-8 w-8 animate-spin" />
            </div>
          ) : sortedContent?.length > 0 ? (
            <div className="space-y-4 overflow-y-auto max-h-[calc(100vh-12rem)] scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent">
              {sortedContent.map((content) => (
                <LongFormContentCard
                  key={content.content_id}
                  content={content}
                  personaData={personaData}
                  onPublish={(contentId) =>
                    publishContentMutation.mutate(
                      { personaId, contentId },
                      {
                        onSuccess: () =>
                          toast({
                            description: "Content published successfully!",
                          }),
                        onError: () =>
                          toast({
                            description:
                              "Error publishing content. Please try again.",
                            variant: "destructive",
                          }),
                      },
                    )
                  }
                  onEdit={() =>
                    setEditState({
                      type: "edit",
                      contentId: content.content_id,
                    })
                  }
                  onAiEdit={() =>
                    setEditState({ type: "ai", contentId: content.content_id })
                  }
                  onStatusChange={handleStatusChange}
                  onDelete={(contentId) =>
                    deleteContentMutation.mutate(
                      { personaId, contentId },
                      {
                        onSuccess: () =>
                          toast({
                            description: "Content deleted successfully!",
                          }),
                        onError: () =>
                          toast({
                            description:
                              "Error deleting content. Please try again.",
                            variant: "destructive",
                          }),
                      },
                    )
                  }
                />
              ))}
            </div>
          ) : (
            <div className="text-center p-12 border rounded-lg bg-muted/20">
              <p className="text-muted-foreground mb-2">
                No content found. Create new content to get started!
              </p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

export default LongFormContentComponent;
